// src/modules/scoring.ts
import { stepValueFor } from './step-table.js';
import { DIMS } from '../constants/dimensions.js';
import type { Dim, Tally, ScoreResult, Answer, Modality, ProfileCode } from '../types/index.js';

const MODALITY_LABELS: Record<number, Modality> = {
  1: '单模态',
  2: '双模态',
  3: '三模态',
  4: '多模态',
};

/**
 * 由四维 tally 计算结果
 */
export function computeFromTally(tally: Tally): ScoreResult {
  for (const d of DIMS) {
    if (typeof tally[d] !== 'number' || tally[d] < 0) {
      throw new Error(`invalid tally for ${d}: ${tally[d]}`);
    }
  }
  const total = DIMS.reduce((s, d) => s + tally[d], 0);
  const step = stepValueFor(total);
  const max = Math.max(...DIMS.map(d => tally[d]));
  const preferred = DIMS.filter(d => max - tally[d] <= step);
  const code = preferred.join('') as ProfileCode;
  const modality = MODALITY_LABELS[preferred.length];
  if (!modality) {
    throw new Error(`unexpected preferred length: ${preferred.length}`);
  }
  return { tally, total, step, preferred: [...preferred], code, modality };
}

/**
 * 从答题数据完整算分
 */
export function score(
  answers: Answer[],
  lookupDim: (qid: number, optId: string) => string,
): ScoreResult {
  if (!Array.isArray(answers) || answers.length === 0) {
    throw new Error('answers must be non-empty array');
  }
  const tally: Tally = { V: 0, A: 0, R: 0, K: 0 };
  for (const q of answers) {
    for (const optId of q.selected || []) {
      const dim = lookupDim(q.questionId, optId);
      if (!isDim(dim)) {
        throw new Error(`invalid dim "${dim}" for q=${q.questionId} opt=${optId}`);
      }
      tally[dim] += 1;
    }
  }
  return computeFromTally(tally);
}

function isDim(x: string): x is Dim {
  return x === 'V' || x === 'A' || x === 'R' || x === 'K';
}
